import { useId, type ElementType, type ReactNode } from 'react';
import { ArrowLeft, Ellipsis } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button, IconButton, buttonVariants, iconButtonVariants } from '../ui/button';
import { Icon, type LucideIcon } from '../ui/icon';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/menu';
import { Tooltip } from '../ui/tooltip';

interface PageHeaderAktion {
  /** Verb zuerst: „Bearbeiten“, „Besichtigung planen“. */
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  /** Löschen und Unumkehrbares; die App öffnet dann einen ConfirmDialog. */
  tone?: 'default' | 'danger';
  /** Läuft: am Rechner Button mit Spinner, im Menü gesperrt mit Spinner statt Icon. */
  loading?: boolean;
  /**
   * Nur am Handy, im Menü „…“. Für Aktionen, die am Rechner schon anderswo
   * stehen, etwa Abmelden im Sidebar-Fuß einer App ohne Navigation am Handy
   * (Konto, AppShell `mobilNav={false}`).
   */
  nurHandy?: boolean;
  /**
   * Geht gerade nicht, mit Grund: „Das Radar ist ausgeschaltet.“ Die Aktion
   * bleibt sichtbar und gesperrt, der Grund steht dabei — am Rechner als
   * Tooltip am Knopf (der Knopf bleibt mit der Tastatur erreichbar), im Menü
   * „…“ am Handy als zweite Zeile, weil es dort keinen Tooltip gibt.
   * Statt eine Aktion wegzulassen, die gleich wieder geht.
   */
  gesperrt?: string;
}

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** `toolbar` (15 px) in Listen und Werkzeugen, `page` (Outfit 22 px) über Detailseiten. */
  size?: 'toolbar' | 'page';
  /** Z. B. <SidebarToggle />. */
  leading?: ReactNode;
  /**
   * Weg zurück, nach `leading` vor dem Titel: ein Pfeil als Link, `label` ist
   * Name für Screenreader und Tooltip („Zurück zu Kunden“). `linkAs` wie bei
   * der Sidebar, z. B. ein Adapter auf react-router. Standard: <a>.
   *
   * `beschriftet`: Pfeil und `label` sichtbar, als eigene Zeile über dem
   * Titel, 44 px auf Touch. Für den Weg zurück in eine andere App, den man
   * ohne Tooltip lesen können muss (Konto: „Zurück zu CRM“).
   */
  zurueck?: { label: string; href: string; linkAs?: ElementType; beschriftet?: boolean };
  /** Kapsel mit Kennzahl und kleinen IconButtons. Unter 768 px in eigener Zeile unter dem Titel. */
  meta?: ReactNode;
  /**
   * Die eine Hauptaktion und ihre Nachbarn. Unter 768 px bleibt sie sichtbar, darum am Handy nur eine;
   * bei `size="page"` steht sie dort unter dem Titel, damit der Name Platz hat.
   */
  actions?: ReactNode;
  /**
   * Weitere Aktionen. Ab 768 px als Buttons vor `actions`, darunter in einem
   * Menü hinter „…“, damit der Kopf am Handy eine Zeile bleibt.
   */
  mehrAktionen?: PageHeaderAktion[];
  /** Zweite Zeile: Tabs oder Filter. */
  children?: ReactNode;
  className?: string;
}

/**
 * Kopf einer Ansicht: Titel, optional Meta-Kapsel und Aktionen, darunter Tabs
 * oder Filter. Unter 768 px eine Titelzeile mit höchstens einer sichtbaren
 * Aktion, der Rest im Menü (README „Mobil“). Über Detailseiten (`page`) darf
 * der Name zwei Zeilen nehmen, auch am Rechner (lange Programmtitel im Radar);
 * am Handy rückt die Aktion darunter.
 */
export function PageHeader({ title, subtitle, size = 'toolbar', leading, zurueck, meta, actions, mehrAktionen, children, className }: PageHeaderProps) {
  const Titel = size === 'page' ? 'h1' : 'h2';
  const mehr = mehrAktionen && mehrAktionen.length > 0 ? mehrAktionen : undefined;
  const mehrAmRechner = mehr?.filter((a) => !a.nurHandy) ?? [];
  return (
    <header className={cn('flex flex-col', className)}>
      {zurueck?.beschriftet && <ZurueckBeschriftet {...zurueck} />}
      <div className="flex min-h-topbar flex-wrap items-center gap-2 py-2 md:gap-3">
        {leading}
        {zurueck && !zurueck.beschriftet && <ZurueckLink {...zurueck} />}
        <div className="min-w-0 flex-1">
          <Titel className={cn(size === 'page' ? 't-page-title line-clamp-2' : 'truncate t-title')}>{title}</Titel>
          {subtitle && <div className="t-small text-muted-foreground">{subtitle}</div>}
        </div>
        {meta && (
          <div className="order-last basis-full md:order-none md:basis-auto">
            <div className="inline-flex h-control-md items-center gap-2 whitespace-nowrap rounded-full border border-border bg-card pl-3.5 pr-1.5 text-[13px] font-medium leading-[18px] [&_button]:size-[26px] [&_button]:rounded-full touch:[&_button]:size-9">
              {meta}
            </div>
          </div>
        )}
        {mehrAmRechner.length > 0 && (
          <div className="hidden items-center gap-2 md:flex">
            {mehrAmRechner.map((a) =>
              a.gesperrt ? (
                <GesperrterKnopf key={a.label} aktion={a} />
              ) : (
                <Button
                  key={a.label}
                  icon={a.icon}
                  variant={a.tone === 'danger' ? 'danger' : 'secondary'}
                  onClick={a.onSelect}
                  loading={a.loading}
                >
                  {a.label}
                </Button>
              ),
            )}
          </div>
        )}
        {actions && (
          <div className={cn('flex items-center gap-2 max-md:shrink-0 md:flex-wrap', size === 'page' && 'max-md:order-last max-md:basis-full')}>{actions}</div>
        )}
        {mehr && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton icon={Ellipsis} label="Weitere Aktionen" variant="ghost" noTooltip className="self-start md:hidden" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {mehr.map((a) =>
                a.gesperrt ? (
                  // Gesperrt, aber lesbar: nicht auf 45 % Deckkraft, sonst
                  // trüge der Grund keinen Kontrast mehr. Wort und Grund in
                  // muted sagen, dass hier gerade nichts geht.
                  <DropdownMenuItem
                    key={a.label}
                    icon={a.icon}
                    disabled
                    className="h-auto py-1.5 data-[disabled]:opacity-100 touch:h-auto touch:min-h-11"
                  >
                    <span className="flex flex-col text-muted-foreground">
                      {a.label}
                      <span className="t-small">{a.gesperrt}</span>
                    </span>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem key={a.label} icon={a.icon} tone={a.tone} onSelect={a.onSelect} loading={a.loading}>
                    {a.label}
                  </DropdownMenuItem>
                ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {children && <div className="flex flex-wrap items-center gap-3">{children}</div>}
    </header>
  );
}

/**
 * Eine gesperrte Aktion am Rechner: `aria-disabled` statt `disabled`, damit
 * der Knopf fokussierbar bleibt und der Tooltip mit dem Grund auch über die
 * Tastatur erscheint; Screenreader lesen den Grund über aria-describedby.
 */
function GesperrterKnopf({ aktion: a }: { aktion: PageHeaderAktion }) {
  const id = useId();
  return (
    <>
      <Tooltip content={a.gesperrt}>
        <Button
          icon={a.icon}
          variant={a.tone === 'danger' ? 'danger' : 'secondary'}
          aria-disabled
          aria-describedby={id}
          className={cn('cursor-not-allowed opacity-45 hover:opacity-45', a.tone !== 'danger' && 'hover:bg-secondary')}
        >
          {a.label}
        </Button>
      </Tooltip>
      <span id={id} hidden>
        {a.gesperrt}
      </span>
    </>
  );
}

function ZurueckBeschriftet({ label, href, linkAs: LinkAs = 'a' }: NonNullable<PageHeaderProps['zurueck']>) {
  // Ghost-Button mit Text; der negative Rand setzt den Pfeil bündig auf die Kante des Inhalts.
  return (
    <LinkAs href={href} className={cn(buttonVariants({ variant: 'ghost' }), '-ml-3.5 mt-2 self-start text-muted-foreground hover:text-foreground')}>
      <Icon icon={ArrowLeft} size={17} />
      {label}
    </LinkAs>
  );
}

function ZurueckLink({ label, href, linkAs: LinkAs = 'a' }: NonNullable<PageHeaderProps['zurueck']>) {
  // Die Hülle trägt den Tooltip: Ein Link-Adapter der App reicht meist keine
  // ref durch, die der Tooltip zum Ausrichten braucht.
  return (
    <Tooltip content={label}>
      <span className="inline-flex">
        <LinkAs href={href} aria-label={label} className={iconButtonVariants({ variant: 'ghost' })}>
          <Icon icon={ArrowLeft} size={17} />
        </LinkAs>
      </span>
    </Tooltip>
  );
}
